/* Crawlable URLs for the public marketing pages.
 * The app itself runs on hash routes (/#/dashboard). The marketing pages also exist at real paths, prerendered to
 * static HTML at build time (scripts/prerender.mjs) so search engines and AI crawlers can read each one without
 * running JavaScript: "/" (home), "/industries/", "/industries/<key>/", "/pricing/", "/video/", "/login/",
 * "/signup/" (the company setup wizard), with Arabic under "/ar/…". The other GCC countries' home and industry pages
 * live under their slug: route "/ae" ⇄ "/ae/", "/ae/industries/garage" ⇄ "/ae/industries/garage/", "/ar/ae/…". */
import { INDUSTRIES } from "../domain/industry";
import { COUNTRIES } from "../domain/country";
import type { Lang } from "../data/types";

/** Marketing routes of one non-Saudi country: its home, industry index and every industry page. */
export const countryRoutes = (slug: string): string[] => ["/" + slug, `/${slug}/industries`, ...INDUSTRIES.map((c) => `/${slug}/industries/${c.key}`)];

const ROUTES = [
  "/home", "/industries", ...INDUSTRIES.map((c) => "/industries/" + c.key), "/pricing", "/video", "/login", "/setup",
  ...COUNTRIES.filter((c) => c.slug).flatMap((c) => countryRoutes(c.slug)),
];
const ROUTE_SET = new Set(ROUTES);

/** Router routes that have a crawlable twin (Saudi pages first, then each other country's pages). */
export function seoRoutes(): string[] {
  return [...ROUTES];
}

/** Routes whose crawlable path is not simply "<route>/". */
const ALIAS: Record<string, string> = { "/home": "/", "/setup": "/signup/" };
const UNALIAS: Record<string, string> = Object.fromEntries(Object.entries(ALIAS).map(([r, p]) => [p.replace(/\/+$/, "") || "/", r]));

export const isSeoRoute = (route: string) => ROUTE_SET.has(route);

/** Router route ("/industries/construction") → crawlable path ("/industries/construction/", "/ar/industries/construction/").
 * Routes without a crawlable twin keep their hash form ("/#/login"). */
export function seoPath(route: string, lang: Lang = "en"): string {
  const [p, q] = route.split("?");
  if (!isSeoRoute(p)) return "/#" + route;
  const base = ALIAS[p] ?? p + "/";
  return (lang === "ar" ? "/ar" + base : base) + (q ? "?" + q : "");
}

/** Crawlable path → { route, lang }, or null when the path is not a marketing page ("/" is the English home). */
export function routeForPath(pathname: string): { route: string; lang: Lang } | null {
  let p = pathname.replace(/\/index\.html$/, "/");
  let lang: Lang = "en";
  if (p === "/ar" || p.startsWith("/ar/")) { lang = "ar"; p = p.slice(3) || "/"; }
  p = p.replace(/\/+$/, "") || "/";
  const route = UNALIAS[p] ?? (ALIAS[p] ? "" : p);
  return isSeoRoute(route) ? { route, lang } : null;
}

/** True when this document was opened at a crawlable sub-page (anything but the root document that hosts the app). */
export function onStaticPage(pathname: string = typeof window === "undefined" ? "/" : window.location.pathname): boolean {
  return pathname !== "/" && pathname !== "/index.html" && routeForPath(pathname) !== null;
}
